'use client';

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import styles from './DarkBridge.module.css';

/* White→black wash spanning Accuracy → Research so the page reads as one
   continuous transition. Progress is driven by Research entry into the
   viewport: 0 at 20% entry, 1 at 60% — same on desktop and mobile. */

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
const START = 0.2;
const END = 0.6;

const BridgeProgressContext = createContext(0);

export function useBridgeProgress() {
  return useContext(BridgeProgressContext);
}

type Props = {
  children: ReactNode;
  targetId?: string;
};

export default function DarkBridge({ children, targetId = 'science-library' }: Props) {
  const bridgeRef = useRef<HTMLDivElement>(null);
  const washRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const bridge = bridgeRef.current;
    const wash = washRef.current;
    if (!bridge || !wash) return undefined;

    const target =
      bridge.querySelector<HTMLElement>(`#${targetId}`) ??
      (bridge.querySelector(`.${styles.content}`)?.lastElementChild as HTMLElement | null);
    if (!target) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const apply = (p: number) => {
      const g = Math.round(255 * (1 - p));
      wash.style.backgroundColor = `rgb(${g}, ${g}, ${g})`;
      bridge.style.setProperty('--bridge-p', String(p));
    };

    const compute = () => {
      const vh = window.innerHeight || 1;
      const entry = 1 - target.getBoundingClientRect().top / vh;
      return clamp01((entry - START) / (END - START));
    };

    let raf = 0;
    let cur = compute();
    let running = false;
    let lastBucket = -1;

    const publish = (p: number) => {
      apply(p);
      const bucket = p > 0.45 ? 1 : 0;
      if (bucket !== lastBucket) {
        lastBucket = bucket;
        setProgress(p);
      }
    };

    const tick = () => {
      const next = compute();
      if (reduce) {
        cur = next;
      } else {
        cur += (next - cur) * 0.18;
        if (Math.abs(next - cur) < 0.0005) cur = next;
      }
      publish(cur);
      raf = requestAnimationFrame(tick);
    };

    publish(cur);

    const io = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) {
          if (!running) {
            running = true;
            raf = requestAnimationFrame(tick);
          }
        } else {
          running = false;
          cancelAnimationFrame(raf);
          publish(target.getBoundingClientRect().top < 0 ? 1 : 0);
        }
      },
      { threshold: 0, rootMargin: '100% 0px 50% 0px' },
    );
    io.observe(target);

    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [targetId]);

  return (
    <BridgeProgressContext.Provider value={progress}>
      <div ref={bridgeRef} className={styles.bridge} style={{ ['--bridge-p' as string]: progress }}>
        <div ref={washRef} className={styles.wash} aria-hidden="true" />
        <div className={styles.content}>{children}</div>
      </div>
    </BridgeProgressContext.Provider>
  );
}
